import { useClerk } from "@clerk/nextjs";
import {
  type JobSalaryClient,
  type MarketQuoteClient,
  MockJobSalaryClient,
  MockMarketQuoteClient,
  StreamingJobSalaryClient,
  StreamingMarketQuoteClient,
  createNatsConnection,
} from "@lossdog/job-watchlists";
import { jetstream } from "@nats-io/jetstream";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { AppConfig } from "@/config/app.config";
import { DemoContext } from "@/providers/demo/DemoContext";
import { logger } from "@/util/logger";

interface JobSalaryContextType {
  // Client and connection only
  jobSalaryClient: JobSalaryClient | null;
  isLoading: boolean;
  marketQuoteClient: MarketQuoteClient | null;
}

const JobSalaryContext = createContext<JobSalaryContextType | undefined>(undefined);

export const JobSalaryProvider = ({ children }: { children: ReactNode }) => {
  // Client state only
  const { loaded: isClerkLoaded, session } = useClerk();
  const { isDemoMode } = DemoContext.useDemo();
  const [isLoading, setIsLoading] = useState(false);
  const [jobSalaryClient, setJobSalaryClient] = useState<JobSalaryClient | null>(null);
  const [marketQuoteClient, setMarketQuoteClient] = useState<MarketQuoteClient | null>(null);

  // Initialize client
  useEffect(() => {
    let mounted = true;
    let currentJobSalaryClient: JobSalaryClient | null = null;
    let currentMarketQuoteClient: MarketQuoteClient | null = null;

    // Only initialize if demo mode OR Clerk is loaded (for non-demo mode)
    if (!isDemoMode && !isClerkLoaded) {
      return () => {
        // No cleanup needed when client is not initialized
      };
    }

    setIsLoading(true);

    async function initClient() {
      try {
        if (isDemoMode) {
          // Use mock client in demo mode (no authentication needed)
          logger.info("[JobSalaryProvider] Initializing mock client for demo mode");
          const mockClient = MockJobSalaryClient.create();
          currentJobSalaryClient = mockClient;

          if (mounted) {
            setJobSalaryClient(mockClient);
            setMarketQuoteClient(new MockMarketQuoteClient());
          }
        } else {
          if (!isClerkLoaded || !session) {
            logger.warn("[JobSalaryProvider] Clerk not loaded or no session, skipping client initialization");
            return;
          }

          // Use real NATS client in authenticated mode
          const token = await session.getToken({ template: "canis-amnis" });
          if (!token) {
            logger.error("Failed to create canis-amnis token");
            return;
          }

          const natsConnection = await createNatsConnection(AppConfig.streamer.url, "individuals", token);
          const jobSalaryClient = await StreamingJobSalaryClient.create(natsConnection);
          await jobSalaryClient.start();
          currentJobSalaryClient = jobSalaryClient;

          const marketQuoteClient = new StreamingMarketQuoteClient(jetstream(natsConnection));

          if (mounted) {
            setJobSalaryClient(jobSalaryClient);
            setMarketQuoteClient(marketQuoteClient);
          }
        }
      } catch (error) {
        logger.error("Failed to initialize JobSalaryClient:", error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    initClient().catch((error) => {
      logger.error("Failed to initialize JobSalaryClient:", error);
      if (mounted) {
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      // Cleanup mock client if it exists
      if (currentJobSalaryClient && "destroy" in currentJobSalaryClient) {
        (currentJobSalaryClient as MockJobSalaryClient).destroy();
        currentMarketQuoteClient && (currentMarketQuoteClient as MockMarketQuoteClient).destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoMode, isClerkLoaded, session]);

  const contextValue: JobSalaryContextType = useMemo(
    () => ({
      // Client and connection only
      jobSalaryClient,
      isLoading,
      marketQuoteClient,
    }),
    [jobSalaryClient, isLoading],
  );

  return <JobSalaryContext.Provider value={contextValue}>{children}</JobSalaryContext.Provider>;
};

export const useJobSalaryContext = () => {
  const context = useContext(JobSalaryContext);
  if (context === undefined) {
    throw new Error("useJobSalaryContext must be used within a JobSalaryProvider");
  }
  return context;
};
